import React from 'react';
import {
  Bell,
  Calculator,
  ClipboardList,
  CreditCard,
  FileText,
  History,
  ListChecks,
  MapPin,
  PackageMinus,
  RefreshCcw,
  ScanLine,
  ShieldCheck,
  ShoppingCart,
  Shuffle,
  Store,
  ToggleLeft,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';

/*
 * Contenido de las páginas de módulo del sitio público
 * (/funcionalidades/<slug>). Cada entrada define el encabezado, el texto,
 * el mock de producto, la banda "Ganá tiempo" y las preguntas frecuentes.
 * El layout vive en src/pages/Modulo.jsx.
 */

const tag = (text, bg, color) => (
  <span className="mock-chip" style={{ background: bg, color }}>{text}</span>
);
const TAG_OK = tag('Cobrado', 'var(--gst-mint-100)', 'var(--gst-mint-700)');
const TAG_PAID = tag('Pagado', 'var(--gst-mint-100)', 'var(--gst-mint-700)');
const TAG_LATE = tag('Vencido', '#FEE2E2', '#991B1B');
const TAG_TODO = tag('A cobrar', 'var(--gst-sand)', '#78350F');
const TAG_TOPAY = tag('A pagar', 'var(--gst-sand)', '#78350F');

const Shimmer = ({ w = 80, right = false }) => (
  <span className="shimmer" style={{ width: w, marginLeft: right ? 'auto' : undefined, display: 'block' }} />
);

const ventas = {
  slug: 'ventas',
  eyebrow: 'Ventas',
  title: 'Cobrá rápido y no perdés el hilo de nada.',
  subtitle: 'Punto de venta, facturación y cuentas corrientes en una sola pantalla, para vender desde el mostrador o varias sucursales sin duplicar información.',
  body: [
    'Gastify centraliza el punto de venta, la facturación y las cuentas corrientes de tus clientes en una sola pantalla. Vendé desde el mostrador o desde varias sucursales sin duplicar información.',
    'Cada venta actualiza stock, caja y cuenta corriente al mismo tiempo, así el cierre de caja deja de ser un ejercicio de memoria.',
  ],
  gana: [
    { icon: FileText, title: 'Facturación AFIP/ARCA en segundos', text: 'Facturá tus ventas sin salir del sistema ni pisar la web de ARCA.' },
    { icon: CreditCard, title: 'Medios de cobro unificados', text: 'Efectivo, tarjeta, transferencia y cheques en una sola caja.' },
    { icon: Users, title: 'Cuentas corrientes claras', text: 'Sabé quién te debe y cuánto hace que no te paga.' },
    { icon: Store, title: 'Multipunto de venta', text: 'Vendé desde el local, el depósito o la tienda online con el mismo stock.' },
  ],
  faqs: [
    { q: '¿Puedo facturar directo a AFIP/ARCA desde Gastify?', a: 'Sí, la facturación electrónica se emite desde el mismo sistema, sin salir a la web de ARCA.' },
    { q: '¿Puedo tener más de un punto de venta?', a: 'Sí, con el módulo multisucursal vendés desde distintos puntos con el mismo stock.' },
    { q: '¿Puedo vender fiado y llevar cuenta corriente?', a: 'Sí, cada cliente tiene su cuenta corriente con saldo, historial de pagos y vencimientos.' },
    { q: '¿Se pueden aplicar descuentos o recargos en una venta?', a: 'Sí, podés aplicar descuentos por producto o por el total de la venta al momento de facturar.' },
    { q: '¿Cómo hago el cierre de caja del día?', a: 'Gastify concilia automáticamente lo cobrado por cada medio de pago para que el arqueo de caja sea inmediato.' },
  ],
  mock: (
    <>
      <div className="mod-mock">
        <div className="mock-bar-head"><span className="title">Ventas</span><span className="mock-btn">Nueva venta</span></div>
        <div className="mock-kpis">
          <div className="mock-kpi"><div className="l">Cantidad</div><div className="v">24</div></div>
          <div className="mock-kpi"><div className="l">Cobrado</div><div className="v">$56.290</div></div>
          <div className="mock-kpi"><div className="l">A cobrar</div><div className="v">$20.500</div></div>
          <div className="mock-kpi"><div className="l">Vencido</div><div className="v">$32.900</div></div>
        </div>
        <table className="mock-table">
          <thead><tr><th>Cliente</th><th>Categoría</th><th>Estado</th></tr></thead>
          <tbody>
            <tr><td>Almacén Norte</td><td>Mayorista</td><td>{TAG_OK}</td></tr>
            <tr><td>Kiosco Delta</td><td>Minorista</td><td>{TAG_LATE}</td></tr>
            <tr><td>Distribuidora SUR</td><td>Mayorista</td><td>{TAG_TODO}</td></tr>
            <tr><td><Shimmer w={88} /></td><td><Shimmer w={64} /></td><td><Shimmer w={48} right /></td></tr>
          </tbody>
        </table>
      </div>
      <div className="mock-float" style={{ bottom: -20, right: -20, width: 208 }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--gst-graphite-900)', marginBottom: 10 }}>Nuevo cobro</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}><Shimmer w={58} /><Shimmer w={46} /></div>
        <div style={{ marginBottom: 12 }}><Shimmer w="100%" /></div>
        <div className="mock-float-actions">
          <span className="mock-pill-btn" style={{ border: '1px solid var(--gst-border-strong)', color: 'var(--gst-graphite-500)' }}>Cancelar</span>
          <span className="mock-pill-btn" style={{ background: 'var(--gst-mint-500)', color: '#fff' }}>Guardar</span>
        </div>
      </div>
    </>
  ),
};

const compras = {
  slug: 'compras',
  eyebrow: 'Compras',
  title: 'Ordená proveedores sin perder el hilo entre pedido y factura.',
  subtitle: 'Órdenes de compra, facturas de proveedores y reposición de stock conectadas en un mismo lugar.',
  body: [
    'Registrá órdenes de compra, cargá facturas de proveedores y dejá que el stock se actualice solo con cada ingreso de mercadería.',
    'Vas a saber cuánto le debés a cada proveedor y cuándo vence cada pago, sin abrir una planilla paralela.',
  ],
  gana: [
    { icon: RefreshCcw, title: 'Costos que se actualizan solos', text: 'Cada compra recalcula el costo real de tus productos.' },
    { icon: Wallet, title: 'Cuenta corriente con proveedores', text: 'Controlá pagos vencidos y por vencer de un vistazo.' },
    { icon: FileText, title: 'Escaneo de facturas', text: 'Cargá comprobantes sin tipear cada línea a mano.' },
    { icon: ClipboardList, title: 'Órdenes de compra', text: 'Armá pedidos y seguí su estado hasta que llega la mercadería.' },
  ],
  faqs: [
    { q: '¿Qué diferencia hay entre una compra y un gasto?', a: 'Una compra suma stock y actualiza tu cuenta corriente con el proveedor; un gasto no mueve inventario.' },
    { q: '¿Puedo ver cuánto le debo a cada proveedor?', a: 'Sí, la cuenta corriente de proveedores se actualiza con cada compra y cada pago que registrás.' },
    { q: '¿Puedo cargar una compra sin factura todavía?', a: 'Sí, podés registrar el ingreso de mercadería y asociar el comprobante después.' },
    { q: '¿Gastify avisa cuándo vence un pago a proveedores?', a: 'Sí, ves de un vistazo qué pagos están vencidos y cuáles vencen próximamente.' },
    { q: '¿Puedo comparar precios entre proveedores?', a: 'Sí, el historial de compras te muestra qué proveedor te vendió cada insumo y a qué precio.' },
  ],
  mock: (
    <>
      <div className="mod-mock">
        <div className="mock-bar-head"><span className="title">Compras</span><span className="mock-btn">Nueva compra</span></div>
        <div className="mock-kpis">
          <div className="mock-kpi"><div className="l">Cantidad</div><div className="v">28</div></div>
          <div className="mock-kpi"><div className="l">Pagado</div><div className="v">$123.493</div></div>
          <div className="mock-kpi"><div className="l">A pagar</div><div className="v">$51.900</div></div>
          <div className="mock-kpi"><div className="l">Vencido</div><div className="v">$27.163</div></div>
        </div>
        <table className="mock-table">
          <thead><tr><th>Proveedor</th><th>Categoría</th><th>Estado</th></tr></thead>
          <tbody>
            <tr><td>Insumos del Litoral</td><td>Materia prima</td><td>{TAG_PAID}</td></tr>
            <tr><td>Distribuidora Andes</td><td>Envases</td><td>{TAG_LATE}</td></tr>
            <tr><td>Logística Cuyo</td><td>Fletes</td><td>{TAG_TOPAY}</td></tr>
            <tr><td><Shimmer w={88} /></td><td><Shimmer w={64} /></td><td><Shimmer w={48} right /></td></tr>
          </tbody>
        </table>
      </div>
      <div className="mock-float" style={{ bottom: -20, left: -20, width: 186 }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--gst-graphite-900)', marginBottom: 10 }}>Pagos</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          <Shimmer w="100%" /><Shimmer w="80%" /><Shimmer w="90%" />
        </div>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--gst-mint-700)' }}>+ Agregar pago</div>
      </div>
    </>
  ),
};

const stock = {
  slug: 'stock',
  eyebrow: 'Stock',
  title: 'Un inventario que se mueve solo con cada operación.',
  subtitle: 'Organizá tus productos en distintos depósitos y mirá en tiempo real cuánto tenés disponible de cada uno.',
  body: [
    'Organizá tus productos en distintos depósitos o sucursales y mirá en tiempo real cuánto tenés disponible de cada uno.',
    'Con cada venta o compra que registrás, el stock sube o baja automáticamente, así dejás de contar productos a mano para saber qué reponer.',
  ],
  gana: [
    { icon: MapPin, title: 'Multidepósito', text: 'Administrá varias sucursales o depósitos desde un mismo panel.' },
    { icon: Shuffle, title: 'Movimientos entre depósitos', text: 'Trasladá mercadería con un clic y mantené la trazabilidad.' },
    { icon: Bell, title: 'Alertas de reposición', text: 'Recibí un aviso antes de que un producto se quede sin stock.' },
    { icon: ScanLine, title: 'Código de barras', text: 'Escaneá productos para vender y reponer más rápido.' },
  ],
  faqs: [
    { q: '¿Puedo tener varios depósitos?', a: 'Sí, podés crear los depósitos que necesites y mover mercadería entre ellos con un clic.' },
    { q: '¿El stock se actualiza solo?', a: 'Sí, cada venta, compra o producción impacta el stock automáticamente.' },
    { q: '¿Puedo ver el historial de movimientos de un producto?', a: 'Sí, cada entrada y salida de stock queda registrada con fecha y motivo.' },
    { q: '¿Gastify sirve si vendo por Tiendanube o Mercado Libre?', a: 'Sí, el stock se sincroniza automáticamente con esas plataformas.' },
    { q: '¿Puedo hacer un ajuste manual de stock?', a: 'Sí, podés corregir cantidades por pérdidas, roturas o diferencias de inventario.' },
  ],
  mock: (
    <>
      <div className="mod-mock">
        <div className="mock-bar-head"><span className="title">Depósitos</span></div>
        <div className="mock-panel-sm">
          <div className="mock-toggle-row"><span className="lbl">Depósito Avellaneda</span><div className="mock-toggle" /></div>
          <div className="mock-toggle-row"><span className="lbl">Depósito Pilar</span><div className="mock-toggle" /></div>
          <div className="mock-toggle-row"><span className="lbl">Movimientos entre depósitos</span><div className="mock-toggle" /></div>
        </div>
        <table className="mock-table" style={{ marginTop: 14 }}>
          <thead><tr><th>Producto</th><th style={{ textAlign: 'right' }}>Stock</th><th style={{ textAlign: 'right' }}>Estado</th></tr></thead>
          <tbody>
            <tr>
              <td>Harina 000 x 25kg</td><td style={{ textAlign: 'right' }}>128</td>
              <td style={{ textAlign: 'right' }}>{tag('OK', 'var(--gst-mint-100)', 'var(--gst-mint-700)')}</td>
            </tr>
            <tr>
              <td>Aceite de girasol</td><td style={{ textAlign: 'right' }}>6</td>
              <td style={{ textAlign: 'right' }}>{tag('Crítico', '#FEE2E2', '#991B1B')}</td>
            </tr>
            <tr><td><Shimmer w={96} /></td><td><Shimmer w={24} right /></td><td><Shimmer w={40} right /></td></tr>
          </tbody>
        </table>
      </div>
      <div className="mock-float" style={{ top: -16, right: -16, width: 158, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 9, background: 'var(--gst-mint-100)', color: 'var(--gst-mint-700)', flexShrink: 0 }}>
          <ScanLine size={16} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 900, color: 'var(--gst-graphite-900)' }}>Código de barras</div>
          <div className="mock-toggle" style={{ marginTop: 6 }} />
        </div>
      </div>
    </>
  ),
};

const produccion = {
  slug: 'produccion',
  eyebrow: 'Producción',
  title: 'Fabricá con el costo real de cada producto, no con una estimación.',
  subtitle: 'Recetas, fórmulas y costos actualizados para negocios que fabrican lo que venden.',
  body: [
    'Armá las recetas o fórmulas de lo que producís y Gastify calcula automáticamente cuánto te cuesta cada unidad, con el precio actualizado de cada insumo.',
    'Registrá cada orden de producción y descontá los insumos usados del stock, sin hacer una cuenta aparte.',
  ],
  gana: [
    { icon: ListChecks, title: 'Recetas y fórmulas', text: 'Definí qué insumos y en qué cantidad usa cada producto.' },
    { icon: Calculator, title: 'Costo real actualizado', text: 'El costo de fabricación cambia solo cuando cambia el precio de un insumo.' },
    { icon: PackageMinus, title: 'Descuento automático de insumos', text: 'Cada producción resta del stock lo que efectivamente se usó.' },
    { icon: ShieldCheck, title: 'Trazabilidad del lote', text: 'Sabé qué se produjo, cuándo y con qué insumos.' },
  ],
  faqs: [
    { q: '¿Cómo calcula Gastify el costo de producción?', a: 'A partir de la receta que armes y el costo actualizado de cada insumo.' },
    { q: '¿Puedo ver qué se produjo en un período?', a: 'Sí, cada orden de producción queda registrada con fecha, insumos y cantidad.' },
    { q: '¿Puedo tener varias recetas para un mismo producto?', a: 'Sí, podés versionar recetas si cambia la fórmula o el proveedor de insumos.' },
    { q: '¿Qué pasa si me falta un insumo para producir?', a: 'Gastify te avisa si el stock disponible no alcanza para completar la orden de producción.' },
    { q: '¿Puedo registrar mermas de producción?', a: 'Sí, podés cargar la merma de cada lote para que el costo real quede reflejado.' },
  ],
  mock: (
    <>
      <div className="mod-mock">
        <div className="mock-bar-head"><span className="title">Receta · Pan de campo x 40u</span></div>
        <table className="mock-table">
          <thead><tr><th>Insumo</th><th style={{ textAlign: 'right' }}>Cantidad</th><th style={{ textAlign: 'right' }}>Subtotal</th></tr></thead>
          <tbody>
            <tr><td>Harina 000</td><td style={{ textAlign: 'right' }}>12 kg</td><td style={{ textAlign: 'right' }}>$9.600</td></tr>
            <tr><td>Levadura</td><td style={{ textAlign: 'right' }}>300 g</td><td style={{ textAlign: 'right' }}>$1.200</td></tr>
            <tr><td>Sal</td><td style={{ textAlign: 'right' }}>200 g</td><td style={{ textAlign: 'right' }}>$180</td></tr>
            <tr><td><Shimmer w={80} /></td><td><Shimmer w={34} right /></td><td><Shimmer w={44} right /></td></tr>
          </tbody>
        </table>
      </div>
      <div className="mock-float" style={{ bottom: -18, right: -18, background: 'var(--gst-graphite-900)', borderColor: 'var(--gst-graphite-900)' }}>
        <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--gst-mint-200)' }}>Costo real x unidad</div>
        <div style={{ marginTop: 6, fontSize: 20, fontWeight: 900, color: '#fff' }}>$274,50</div>
      </div>
    </>
  ),
};

const analisis = {
  slug: 'analisis',
  eyebrow: 'Análisis y rentabilidad',
  title: 'Tomá decisiones con números reales, no con la sensación de cómo va el mes.',
  subtitle: 'Ingresos, egresos y rentabilidad desglosados por cliente, producto o categoría.',
  body: [
    'Mirá tus ingresos, egresos y el resultado del período que necesites, con el detalle desglosado por cliente, producto o categoría.',
    'Personalizá tu informe final para entender de un vistazo si el negocio da ganancia y en qué se te está yendo la plata.',
  ],
  gana: [
    { icon: TrendingUp, title: 'Rentabilidad por producto', text: 'Sabé qué te deja más margen y qué te conviene dejar de vender.' },
    { icon: FileText, title: 'Reporte final personalizado', text: 'Armá el informe con las cuentas que te importan a vos.' },
    { icon: ShoppingCart, title: 'Ranking de productos y clientes', text: 'Descubrí quién te compra más y qué se vende mejor.' },
    { icon: History, title: 'Historial de stock', text: 'Consultá movimientos pasados sin perder el dato.' },
  ],
  faqs: [
    { q: '¿Dónde veo la rentabilidad de mi negocio?', a: 'En el módulo de informes, con el desglose por producto, cliente o categoría que elijas.' },
    { q: '¿Puedo armar un informe personalizado?', a: 'Sí, elegís qué cuentas incluir y para qué período.' },
    { q: '¿Puedo comparar un mes con otro?', a: 'Sí, el reporte final te deja elegir el período y compararlo con el anterior.' },
    { q: '¿Puedo exportar los informes?', a: 'Sí, podés exportar tus informes para compartirlos con tu contador o tu equipo.' },
    { q: '¿Se puede ver la rentabilidad por sucursal?', a: 'Sí, si tenés más de un punto de venta, podés desglosar los resultados por sucursal.' },
  ],
  mock: (
    <>
      <div className="mod-mock">
        <div className="mock-bar-head"><span className="title">Resultado · últimos 6 meses</span></div>
        <div className="mock-bars">
          {[40, 55, 48, 66, 60, 78].map((v, i) => (
            <div
              key={i}
              style={{
                height: `${v}%`,
                background: i === 5 ? 'var(--gst-mint-500)' : 'var(--gst-cream-100)',
                border: '1px solid var(--gst-border)',
              }}
            />
          ))}
        </div>
        <table className="mock-table" style={{ marginTop: 14 }}>
          <thead><tr><th>Producto</th><th style={{ textAlign: 'right' }}>Ranking</th></tr></thead>
          <tbody>
            <tr><td>Combo desayuno</td><td style={{ textAlign: 'right' }}>#1</td></tr>
            <tr><td>Pan de campo</td><td style={{ textAlign: 'right' }}>#2</td></tr>
            <tr><td><Shimmer w={100} /></td><td><Shimmer w={20} right /></td></tr>
          </tbody>
        </table>
      </div>
      <div className="mock-float" style={{ top: -16, right: -16 }}>
        <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--gst-graphite-500)' }}>Resultado</div>
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <TrendingUp size={16} style={{ color: 'var(--gst-mint-600)' }} />
          <span style={{ fontSize: 16, fontWeight: 900, color: 'var(--gst-graphite-900)' }}>+34%</span>
        </div>
      </div>
    </>
  ),
};

const integraciones = {
  slug: 'integraciones',
  eyebrow: 'Integraciones',
  title: 'Conectá Gastify con las herramientas que ya usás.',
  subtitle: 'Sincronizá tu tienda online, facturá directo a AFIP/ARCA y bajá tus ventas de Mercado Libre sin cargar nada a mano.',
  body: [
    'Gastify se conecta con AFIP/ARCA para facturar electrónicamente sin salir del sistema, y con Mercado Libre y Tiendanube para que tus ventas y tu stock viajen solos entre plataformas.',
    'Activás cada integración con un clic, sin desarrollo a medida ni configuraciones técnicas.',
  ],
  gana: [
    { icon: FileText, title: 'Facturación electrónica AFIP/ARCA', text: 'Facturá directamente desde Gastify, sin ingresar a la web de ARCA.' },
    { icon: ShoppingCart, title: 'Mercado Libre', text: 'Bajá tus órdenes de venta automáticamente y llevá un control único de todas tus ventas.' },
    { icon: Store, title: 'Tiendanube', text: 'Sincronizá productos, precios y stock entre tu tienda y Gastify, en ambos sentidos.' },
    { icon: ToggleLeft, title: 'Activación en un clic', text: 'Prendé o apagá cada integración cuando la necesites, sin soporte técnico.' },
  ],
  faqs: [
    { q: '¿Necesito conocimientos técnicos para activar una integración?', a: 'No, se activa con un interruptor desde la configuración de tu cuenta.' },
    { q: '¿Qué pasa si desactivo una integración?', a: 'Dejás de sincronizar datos nuevos, pero no se borra lo que ya se sincronizó.' },
    { q: '¿Puedo facturar por AFIP/ARCA sin entrar a su web?', a: 'Sí, Gastify emite la factura electrónica directamente desde el sistema.' },
    { q: '¿El stock se actualiza en ambos sentidos con Tiendanube?', a: 'Sí, un cambio en Gastify o en tu tienda se refleja automáticamente en el otro.' },
    { q: '¿Bajan solas las ventas de Mercado Libre?', a: 'Sí, cada orden se importa a Gastify sin que tengas que cargarla a mano.' },
  ],
  mock: (
    <>
      <div className="mod-mock">
        <div className="mock-bar-head"><span className="title">Integraciones</span></div>
        <div className="mock-toggle-row">
          <div className="lbl"><span className="chip"><FileText size={15} /></span>Facturación AFIP/ARCA</div>
          <div className="mock-toggle" />
        </div>
        <div className="mock-toggle-row">
          <div className="lbl"><span className="chip"><ShoppingCart size={15} /></span>Mercado Libre</div>
          <div className="mock-toggle off" />
        </div>
        <div className="mock-toggle-row">
          <div className="lbl"><span className="chip"><Store size={15} /></span>Tiendanube</div>
          <div className="mock-toggle" />
        </div>
        <div className="mock-flow">
          <span className="node" /><span className="line" /><span className="node" /><span className="line" /><span className="node" />
        </div>
      </div>
      <div className="mock-float" style={{ bottom: -18, right: -18 }}>
        <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gst-graphite-500)' }}>Sincronizado</div>
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCcw size={14} style={{ color: 'var(--gst-mint-600)' }} />
          <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--gst-graphite-900)' }}>hace 2 minutos</span>
        </div>
      </div>
    </>
  ),
};

export const MODULE_PAGES = { ventas, compras, stock, produccion, analisis, integraciones };

export const getModulePage = (slug) => MODULE_PAGES[slug] || null;

export default MODULE_PAGES;
